import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import ClaimLocationMap from '../components/ClaimLocationMap';
import FileUpload from '../components/FileUpload';

const NewClaimPage: React.FC = () => {
  const [attachments, setAttachments] = useState<File[]>([]);
  // New state for file number
  const [fileNumber, setFileNumber] = useState<string>('');
  const [loadingFileNumber, setLoadingFileNumber] = useState<boolean>(true);

  // New dropdown and field state
  const [insurers, setInsurers] = useState<any[]>([]);
  const [loadingInsurers, setLoadingInsurers] = useState(false);
  const [representatives, setRepresentatives] = useState<any[]>([]);
  const [loadingReps, setLoadingReps] = useState(false);
  const [adjusters, setAdjusters] = useState<any[]>([]);
  const [loadingAdjusters, setLoadingAdjusters] = useState(false);

  // Claim/client form state
  const [claimForm, setClaimForm] = useState({
    claimNumber: '',
    status: 'OPEN',
    description: '',
    amount: '',
    type: '',
    dateOfLoss: '',
    dateReported: '',
    insuredName: '',
    adjusterName: '',
    policyNumber: '',
    location: '',
    claimAddress: '',
    latitude: '',
    longitude: '',
    note: '',
    insurerId: '',
    contactId: '',
    companyId: '',
    representativeId: '',
    priorPayments: '',
    paCommission: ''
  });

  // Client form state
  const [clientType, setClientType] = useState<'existing' | 'new'>('existing');
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientForm, setClientForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [loadingClients, setLoadingClients] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch insurers on mount
  useEffect(() => {
    setLoadingInsurers(true);
    api.get('/insurers/dropdown')
      .then(res => {
        console.log('Insurers loaded:', res.data.data?.length || 0, 'companies');
        setInsurers((res.data.data || []).sort((a: any, b: any) => a.name.localeCompare(b.name)));
      })
      .catch(err => {
        console.error('Error loading insurers:', err);
        setInsurers([]);
      })
      .finally(() => setLoadingInsurers(false));
  }, []);

  // Fetch insurance reps when insurer changes
  useEffect(() => {
    if (!claimForm.insurerId) {
      setRepresentatives([]);
      return;
    }
    setLoadingReps(true);
            api.get(`/insurance-reps?insurerId=${claimForm.insurerId}&limit=100`)
      .then(res => {
        const sorted = (res.data.data || []).sort((a: any, b: any) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setRepresentatives(sorted);
      })
      .catch(() => setRepresentatives([]))
      .finally(() => setLoadingReps(false));
  }, [claimForm.insurerId]);

  // Fetch adjusters on mount
  useEffect(() => {
    setLoadingAdjusters(true);
    api.get('/users?limit=1000')
      .then(res => {
        const users = res.data.data || [];
        const adjusters = users.filter((u: any) => (u.role || u.title || '').toLowerCase().includes('adjuster'));
        setAdjusters(adjusters.sort((a: any, b: any) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)));
      })
      .catch(() => setAdjusters([]))
      .finally(() => setLoadingAdjusters(false));
  }, []);

  // Helper to extract numeric part and increment
  const getNextFileNumber = (claims: any[]) => {
    // Try to find max file_number (or claimNumber if that's the convention)
    let maxNum = 0;
    claims.forEach((claim: any) => {
      const num = parseInt((claim.file_number || claim.fileNumber || claim.claimNumber || '').replace(/[^0-9]/g, ''), 10);
      if (!isNaN(num) && num > maxNum) maxNum = num;
    });
    // Format as desired, e.g., CLM-<number+1> or just next number
    return maxNum ? `CLM-${(maxNum + 1).toString().padStart(5, '0')}` : 'CLM-00001';
  };

  // Fetch all claims on mount to determine next file number
  useEffect(() => {
    setLoadingFileNumber(true);
    api.get('/claims?limit=1000')
      .then(res => {
        const claims = res.data.data || [];
        const next = getNextFileNumber(claims);
        setFileNumber(next);
        setClaimForm(prev => ({ ...prev, fileNumber: next, claimNumber: next }));
      })
      .catch(() => {
        setFileNumber('CLM-00001');
        setClaimForm(prev => ({ ...prev, fileNumber: 'CLM-00001', claimNumber: 'CLM-00001' }));
      })
      .finally(() => setLoadingFileNumber(false));
  }, []);
  const navigate = useNavigate();

  useEffect(() => {
    if (clientType === 'existing') {
      setLoadingClients(true);
      api.get('/clients?sort=lastName:asc&limit=1000')
        .then(res => {
          console.log('Clients loaded:', res.data.data);
          setClients(res.data.data || []);
        })
        .catch(err => {
          console.error('Error loading clients:', err);
          setClients([]);
        })
        .finally(() => setLoadingClients(false));
    }
  }, [clientType]);

  const handleClientFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientForm({ ...clientForm, [e.target.name]: e.target.value });
  };

  const handleClaimFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClaimForm({ ...claimForm, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name: string, value: any) => {
    console.log(`Setting ${name} to:`, value);
    setClaimForm({ ...claimForm, [name]: value });
    
    // If insurance company changed, reset representative and fetch new ones
    if (name === 'insurerId') {
      setClaimForm(prev => ({ ...prev, [name]: value, representativeId: '' }));
      if (value) {
        setLoadingReps(true);
        api.get(`/insurance-reps?insurerId=${value}&limit=1000`)
          .then(res => {
            console.log('Fetched representatives for insurer:', res.data.data);
            console.log('Number of representatives found:', res.data.data?.length || 0);
            setRepresentatives(res.data.data || []);
          })
          .catch(err => {
            console.error('Error loading representatives:', err);
            setRepresentatives([]);
          })
          .finally(() => setLoadingReps(false));
      } else {
        setRepresentatives([]);
      }
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    console.log('🚀 FORM SUBMITTED - handleCreate called!');
    e.preventDefault();
    
    // Check authentication first
    const token = localStorage.getItem('token');
    console.log('🔐 Auth token exists:', !!token);
    console.log('🔐 Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
    
    if (!token) {
      console.error('❌ No authentication token found!');
      setError('Please log in to create claims');
      return;
    }
    
    console.log('=== CLAIM CREATION STARTED ===');
    console.log('Current claimForm state:', claimForm);
    console.log('Current clientType:', clientType);
    console.log('Current selectedClientId:', selectedClientId);
    console.log('Current clientForm:', clientForm);
    
    // Add a simple validation check
    if (clientType === 'existing' && !selectedClientId) {
      console.error('❌ Validation Error: No client selected');
      setError('Please select a client');
      return;
    }
    
    if (clientType === 'new' && (!clientForm.firstName || !clientForm.lastName)) {
      console.error('❌ Validation Error: Missing client name');
      setError('Please enter client first and last name');
      return;
    }
    
    setCreating(true);
    setError(null);
    try {
      // Check authentication
      const token = localStorage.getItem('token');
      console.log('Auth token exists:', !!token);
      
      let clientId = selectedClientId;
      if (clientType === 'new') {
        console.log('Creating new client:', clientForm);
        const clientRes = await api.post('/clients', clientForm);
        clientId = clientRes.data.data.id;
        console.log('New client created with ID:', clientId);
      } else {
        console.log('Using existing client ID:', clientId);
        if (!clientId) {
          throw new Error('Please select a client');
        }
      }
      
      // Map frontend form fields to database schema
      const claimPayload = {
        claimNumber: claimForm.claimNumber || fileNumber, // Use fileNumber as claimNumber
        file_number: fileNumber, // Database uses file_number
        status: claimForm.status || 'OPEN',
        description: claimForm.description,
        amount: claimForm.amount ? parseFloat(claimForm.amount) : null,
        type: claimForm.type,
        dateOfLoss: claimForm.dateOfLoss ? new Date(claimForm.dateOfLoss) : null,
        dateReported: claimForm.dateReported ? new Date(claimForm.dateReported) : new Date(),
        insuredName: claimForm.insuredName,
        adjusterName: claimForm.adjusterName,
        policyNumber: claimForm.policyNumber,
        location: claimForm.location,
        claimAddress: claimForm.claimAddress,
        latitude: claimForm.latitude ? parseFloat(claimForm.latitude) : null,
        longitude: claimForm.longitude ? parseFloat(claimForm.longitude) : null,
        note: claimForm.note,
        // Required fields
        clientId: clientId, // Required
        insurerId: claimForm.insurerId, // Required - from dropdown
        tenantId: null, // Will be set by backend from req.user.tenantId
        // Optional fields
        contactId: claimForm.contactId || null,
        companyId: claimForm.companyId || null,
        representativeId: claimForm.representativeId || null
      };
      
      // Validate required fields before sending
      if (!claimPayload.claimNumber) {
        throw new Error('Claim number is required');
      }
      if (!claimPayload.clientId) {
        throw new Error('Client is required');
      }
      if (!claimPayload.insurerId) {
        throw new Error('Insurance company is required');
      }
      
      console.log('Creating claim with payload:', claimPayload);
      console.log('Representative ID being sent:', claimForm.representativeId);
      console.log('Full claim form state:', claimForm);
      
      const claimRes = await api.post('/claims', claimPayload);
      console.log('Claim created successfully:', claimRes.data);
      navigate('/claims');
    } catch (err: any) {
      console.error('Error creating claim:', err);
      console.error('Error response:', err?.response?.data);
      console.error('Error status:', err?.response?.status);
      console.error('Full error object:', JSON.stringify(err?.response?.data, null, 2));
      setError(err?.response?.data?.message || err?.message || 'Failed to create claim');
    } finally {
      setCreating(false);
    }
  };
    
    return (
    <Paper sx={{ maxWidth: 'xl', mx: 'auto', mt: 4, p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: 3, background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)' }}>
      <form onSubmit={handleCreate} style={{ width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 800, letterSpacing: 0.5 }}>Open New Claim</Typography>
      <Divider sx={{ mb: 4 }} />

      {/* Insured Details */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>Insured Details</Typography>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Client Type</FormLabel>
          <RadioGroup
            row
            name="clientType"
            value={clientType}
            onChange={(e) => setClientType(e.target.value as 'existing' | 'new')}
          >
            <FormControlLabel value="existing" control={<Radio />} label="Existing Client" />
            <FormControlLabel value="new" control={<Radio />} label="New Client" />
          </RadioGroup>
        </FormControl>
        {clientType === 'existing' ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel>Select Existing Client</FormLabel>
            <Select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              displayEmpty
              required
            >
              <MenuItem value="">Select client...</MenuItem>
              {loadingClients ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : (
                clients.map((client: any) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.lastName}, {client.firstName} ({client.email})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        ) : (
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <TextField
                label="First Name"
                name="firstName"
                value={clientForm.firstName}
                onChange={handleClientFormChange}
                fullWidth
                sx={{ mb: { xs: 2, md: 0 } }}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                label="Last Name"
                name="lastName"
                value={clientForm.lastName}
                onChange={handleClientFormChange}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                value={clientForm.email}
                onChange={handleClientFormChange}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={6}>
              <TextField
                label="Phone"
                name="phone"
                value={clientForm.phone}
                onChange={handleClientFormChange}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </Card>

      {/* Insurer Details */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>Insurer Details</Typography>
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <FormControl fullWidth required>
              <FormLabel>Insurance Company</FormLabel>
              <Select
                name="insurerId"
                value={claimForm.insurerId}
                onChange={(e) => handleSelectChange('insurerId', e.target.value)}
                displayEmpty
              >
                <MenuItem value="">Select insurance company...</MenuItem>
                {loadingInsurers ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  insurers.map((ins: any) => (
                    <MenuItem key={ins.id} value={ins.id}>{ins.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Insurance Representative</FormLabel>
              <Select
                name="representativeId"
                value={claimForm.representativeId}
                onChange={(e) => handleSelectChange('representativeId', e.target.value)}
                displayEmpty
                disabled={!claimForm.insurerId}
              >
                <MenuItem value="">Select insurance rep...</MenuItem>
                {loadingReps ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  representatives.map((rep: any) => (
                    <MenuItem key={rep.id} value={rep.id}>{rep.firstName} {rep.lastName}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Claim Details */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>Claim Details</Typography>
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <TextField
              label="File Number"
              name="fileNumber"
              value={fileNumber}
              fullWidth
              required
              InputProps={{ readOnly: true }}
              helperText={loadingFileNumber ? 'Loading next available file number...' : 'Auto-generated, unique file number.'}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <TextField
              label="Claim Number"
              name="claimNumber"
              value={claimForm.claimNumber}
              onChange={handleClaimFormChange}
              fullWidth
              required
              helperText="Required. Unique reference for the claim."
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Status</FormLabel>
              <Select
                name="status"
                value={claimForm.status}
                onChange={handleClaimFormChange}
              >
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Claim Type</FormLabel>
              <Select
                name="type"
                value={claimForm.type}
                onChange={handleClaimFormChange}
                displayEmpty
              >
                <MenuItem value="">Select type...</MenuItem>
                <MenuItem value="PROPERTY">Property</MenuItem>
                <MenuItem value="LIABILITY">Liability</MenuItem>
                <MenuItem value="HEALTH">Health</MenuItem>
                <MenuItem value="AUTO">Auto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} md={6}>
            <TextField
              label="Amount"
              name="amount"
              value={claimForm.amount}
              onChange={handleClaimFormChange}
              fullWidth
              type="number"
              helperText="Optional. Claimed amount."
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              label="Description"
              name="description"
              value={claimForm.description}
              onChange={handleClaimFormChange}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
              helperText="Optional. Brief description of the claim."
            />
          </Grid>
        </Grid>
      </Card>

      {/* Claim Address and Location */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>Claim Address and Location</Typography>
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <TextField
              label="Address of Claim"
              name="claimAddress"
              value={claimForm.claimAddress}
              onChange={handleClaimFormChange}
              fullWidth
              required
              helperText="Physical address where the claim occurred."
            />
          </Grid>
          <Grid xs={12}>
            <ClaimLocationMap
              lat={claimForm.latitude}
              lng={claimForm.longitude}
              onChange={(lat, lng) => {
                setClaimForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
              }}
            />
          </Grid>
        </Grid>
      </Card>

      {/* Payments & Deductions */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>Payments & Deductions</Typography>
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <TextField
              label="Prior Payments"
              name="priorPayments"
              type="number"
              value={claimForm.priorPayments || ''}
              onChange={handleClaimFormChange}
              fullWidth
              helperText="Payments made by insurance company prior to this claim."
            />
          </Grid>
          <Grid xs={12} md={6}>
            <TextField
              label="PA Commission (%)"
              name="paCommission"
              type="number"
              value={claimForm.paCommission || ''}
              onChange={handleClaimFormChange}
              fullWidth
              helperText="Public Adjuster commission percentage."
            />
          </Grid>
        </Grid>
      </Card>

      {/* File Upload */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>Attachments</Typography>
        <FileUpload files={attachments} onChange={setAttachments} />
      </Card>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={creating}
          sx={{ mt: 2 }}
          onClick={() => console.log('Submit button clicked!')}
        >
          {creating ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </form>
    </Paper>
  );
};
export default NewClaimPage;
